import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { normalizeUiLang, persistManualLanguage } from '../../utils/localePreference';
import { applySeo } from '../../utils/seo';
import { ogBrandImageUrl } from '../../constants/ogBrand';
import CardOwnerSignupModal from './CardOwnerSignupModal';
import TcardsBrandLogo from '../TcardsBrandLogo';
import { TCARDS_HOME_URL } from './miniWebsitePaths';
import {
  PAID_UNIFIED_PLANS,
  getUnifiedPlanPrice,
  getDisplayCurrencyForLang,
  formatPlanPrice,
  UNIFIED_PLAN_META,
  type PaidUnifiedPlanKey,
  type UnifiedBillingCycle,
} from '../../config/unifiedPlans';

const LANG_OPTIONS = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'sw', label: 'Kiswahili', flag: '🇹🇿' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
];

const SAMPLE_WEBSITES = {
  product: {
    url: 'https://jamilashop.tcards.org/web',
    labelKey: 'mwl_sample_product_btn',
    defaultLabel: 'Product Mini Website',
    icon: '📦',
    accent: '#2563EB',
    soft: '#EFF6FF',
  },
  service: {
    url: 'https://alex.tcards.org/web',
    labelKey: 'mwl_sample_service_btn',
    defaultLabel: 'Service Mini Website',
    icon: '⚡',
    accent: '#7C3AED',
    soft: '#F5F3FF',
  },
} as const;

type SamplePreview = { url: string; title: string } | null;

const landingStyles = `
.mwl-root { font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif; color: #1E293B; background: #FAFBFC; overflow-x: hidden; }
.mwl-nav { display: flex; justify-content: space-between; align-items: center; gap: 12px; padding: 12px 16px; background: #FFF; border-bottom: 1px solid #E2E8F0; position: sticky; top: 0; z-index: 50; }
.mwl-brand { display: flex; align-items: center; gap: 10px; text-decoration: none; min-width: 0; }
.mwl-brand-text { min-width: 0; }
.mwl-brand-title { font-weight: 800; font-size: 18px; color: #111827; white-space: nowrap; }
.mwl-brand-tag { font-size: 13px; color: #6B7280; display: none; }
.mwl-nav-actions { display: none; align-items: center; gap: 10px; }
.mwl-nav-actions--open { display: flex; flex-direction: column; align-items: stretch; position: absolute; top: 100%; left: 0; right: 0; background: #FFF; border-bottom: 1px solid #E2E8F0; padding: 12px 16px 16px; box-shadow: 0 12px 32px rgba(15,23,42,0.08); }
.mwl-nav-row { display: flex; align-items: center; gap: 8px; margin-left: auto; flex-shrink: 0; }
.mwl-menu-btn { display: inline-flex; align-items: center; justify-content: center; width: 42px; height: 42px; border: 1px solid #E2E8F0; border-radius: 10px; background: #FFF; cursor: pointer; font-size: 20px; }
.mwl-section { padding: 48px 16px; }
.mwl-section--hero { text-align: center; padding: 56px 16px 48px; background: linear-gradient(180deg, #EFF6FF 0%, #FAFBFC 100%); }
.mwl-section--white { background: #FFF; }
.mwl-section--dark { background: linear-gradient(135deg, #1E293B, #0F172A); color: #FFF; }
.mwl-section--cta { background: linear-gradient(135deg, #3B82F6, #7C3AED); color: #FFF; text-align: center; }
.mwl-container { max-width: 960px; margin: 0 auto; }
.mwl-container--narrow { max-width: 680px; margin: 0 auto; }
.mwl-container--contact { max-width: 520px; margin: 0 auto; }
.mwl-hero-title { font-size: clamp(28px, 7vw, 48px); font-weight: 900; line-height: 1.12; margin: 0 auto 16px; max-width: 720px; }
.mwl-hero-sub { font-size: clamp(15px, 4vw, 19px); color: #475569; max-width: 560px; margin: 0 auto 28px; line-height: 1.65; }
.mwl-grid-2 { display: grid; grid-template-columns: 1fr; gap: 16px; }
.mwl-grid-benefits { display: grid; grid-template-columns: 1fr; gap: 16px; }
.mwl-container--pricing { max-width: 1140px; }
.mwl-pricing-grid { display: grid; grid-template-columns: 1fr; gap: 16px; max-width: 1140px; margin: 0 auto; align-items: stretch; }
.mwl-billing-toggle { display: flex; gap: 6px; background: #F1F5F9; border-radius: 12px; padding: 4px; max-width: 420px; margin: 0 auto 24px; overflow-x: auto; }
.mwl-billing-btn { flex: 1; min-width: 92px; padding: 10px 8px; border: none; border-radius: 10px; font-size: 13px; font-weight: 600; cursor: pointer; background: transparent; color: #64748B; white-space: nowrap; }
.mwl-billing-btn--active { background: #FFF; color: #0F172A; box-shadow: 0 1px 3px rgba(0,0,0,0.08); }
.mwl-price-card { border-radius: 18px; padding: 22px 20px; border: 1.5px solid #E2E8F0; background: #FFF; display: flex; flex-direction: column; min-height: 100%; }
.mwl-price-card--popular { border-color: #8B5CF6; box-shadow: 0 12px 36px rgba(124,58,237,0.12); }
.mwl-btn { display: inline-flex; align-items: center; justify-content: center; gap: 8px; min-height: 48px; padding: 14px 24px; border-radius: 14px; font-weight: 700; font-size: 16px; cursor: pointer; border: none; width: 100%; max-width: 100%; box-sizing: border-box; }
.mwl-btn--primary { background: linear-gradient(135deg, #3B82F6, #7C3AED); color: #FFF; box-shadow: 0 8px 28px rgba(59,130,246,0.28); }
.mwl-btn--ghost { background: #FFF; color: #3B82F6; border: 1.5px solid #BFDBFE; }
.mwl-btn--white { background: #FFF; color: #3B82F6; }
.mwl-btn--success { background: linear-gradient(135deg, #10B981, #059669); color: #FFF; }
.mwl-btn--inline { width: auto; min-width: 140px; }
.mwl-chip-row { display: flex; flex-wrap: wrap; justify-content: center; gap: 10px; }
.mwl-chip { padding: 10px 18px; background: #EFF6FF; border-radius: 999px; font-size: 14px; font-weight: 600; color: #3B82F6; border: 1px solid #BFDBFE; }
.mwl-step { display: flex; align-items: flex-start; gap: 14px; }
.mwl-step-num { width: 44px; height: 44px; border-radius: 50%; background: linear-gradient(135deg, #3B82F6, #7C3AED); color: #FFF; display: flex; align-items: center; justify-content: center; font-weight: 800; font-size: 18px; flex-shrink: 0; }
.mwl-form { display: flex; flex-direction: column; gap: 14px; }
.mwl-input, .mwl-textarea { width: 100%; padding: 14px 16px; border: 1.5px solid #E2E8F0; border-radius: 12px; font-size: 16px; outline: none; box-sizing: border-box; }
.mwl-textarea { resize: vertical; min-height: 120px; }
.mwl-faq { margin-bottom: 16px; padding: 18px 16px; background: #FFF; border-radius: 14px; border: 1px solid #E2E8F0; }
.mwl-samples-grid { display: grid; grid-template-columns: 1fr; gap: 16px; max-width: 800px; margin: 0 auto; align-items: stretch; }
.mwl-sample-card { display: flex; flex-direction: column; height: 100%; border-radius: 18px; padding: 22px 20px; border: 1.5px solid #E2E8F0; background: #FFF; text-align: center; box-sizing: border-box; }
.mwl-sample-card-desc { flex: 1; margin: 0 0 16px; font-size: 13px; color: #64748B; line-height: 1.5; min-height: 3em; display: flex; align-items: flex-start; justify-content: center; }
.mwl-sample-btn { display: flex; align-items: center; justify-content: center; gap: 8px; width: 100%; min-height: 48px; margin-top: auto; padding: 14px 16px; border-radius: 14px; font-weight: 700; font-size: 14px; line-height: 1.35; cursor: pointer; border: none; color: #FFF; box-sizing: border-box; text-align: center; }
.mwl-preview-overlay { position: fixed; inset: 0; z-index: 200; background: rgba(15, 23, 42, 0.65); display: flex; align-items: center; justify-content: center; padding: 12px; box-sizing: border-box; }
.mwl-preview-panel { background: #FFF; border-radius: 16px; width: min(1100px, 100%); height: min(92vh, 900px); display: flex; flex-direction: column; overflow: hidden; box-shadow: 0 25px 60px rgba(0,0,0,0.35); }
.mwl-preview-header { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 12px 16px; border-bottom: 1px solid #E2E8F0; background: linear-gradient(135deg, #EFF6FF, #F5F3FF); flex-shrink: 0; }
.mwl-preview-frame { flex: 1; border: none; width: 100%; background: #F8FAFC; }
.mwl-nav-desktop { display: none; }
@media (min-width: 640px) {
  .mwl-samples-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 20px; }
}
@media (min-width: 768px) {
  .mwl-nav { padding: 14px 24px; position: sticky; }
  .mwl-brand-tag { display: block; }
  .mwl-menu-btn { display: none; }
  .mwl-nav-desktop { display: flex; align-items: center; gap: 12px; }
  .mwl-nav-actions { display: none !important; }
  .mwl-section { padding: 56px 24px; }
  .mwl-section--hero { padding: 72px 24px 56px; }
  .mwl-grid-2 { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 20px; }
  .mwl-grid-benefits { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 20px; }
  .mwl-grid-4 { grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 24px; }
  .mwl-pricing-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 18px; }
  .mwl-price-card { padding: 20px 16px; }
  .mwl-btn { width: auto; }
  .mwl-btn--block-desktop { display: inline-flex; width: auto; min-width: 220px; }
}
@media (min-width: 900px) {
  .mwl-grid-benefits { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .mwl-grid-4 { grid-template-columns: repeat(4, minmax(0, 1fr)); }
  .mwl-pricing-grid { gap: 24px; }
  .mwl-price-card { padding: 22px 20px; }
}
`;

const MiniWebsiteLanding: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const resolvedLang = normalizeUiLang(i18n.resolvedLanguage || i18n.language);

  const goCreate = () => navigate('/website/login');
  const [showSignup, setShowSignup] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [billingCycle, setBillingCycle] = useState<UnifiedBillingCycle>('monthly');

  const [contactForm, setContactForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitMsg, setSubmitMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
  const [samplePreview, setSamplePreview] = useState<SamplePreview>(null);

  useEffect(() => {
    if (!samplePreview) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setSamplePreview(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [samplePreview]);

  useEffect(() => {
    document.body.style.overflow = samplePreview ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [samplePreview]);

  useEffect(() => {
    if (!menuOpen) return;
    const onResize = () => setMenuOpen(false);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [menuOpen]);

  useEffect(() => {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://www.tcards.org';
    const title = `${t('mwl_hero_title')} | Tcards Mini Website`;
    const description = t('mwl_hero_subtitle');
    return applySeo({
      title,
      description,
      image: ogBrandImageUrl(origin, 'miniwebsite'),
      imageAlt: 'Tcards Mini Website',
      url: `${origin}/miniwebsite`,
      canonicalUrl: `${origin}/miniwebsite`,
      siteName: 'Tcards Mini Website',
    });
  }, [t, resolvedLang]);

  const handleContact = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    if (!contactForm.name || !contactForm.email || !contactForm.phone || !contactForm.message) {
      setSubmitMsg({ type: 'err', text: t('mwl_contact_fill_all') });
      return;
    }
    setSubmitting(true);
    setSubmitMsg(null);
    try {
      const res = await fetch('/api/leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cardOwnerId: 'homepage',
          cardOwnerEmail: 'miniwebsite@tcards.org',
          cardOwnerName: 'Tcards Mini Website',
          fullName: contactForm.name,
          email: contactForm.email,
          phoneNumber: contactForm.phone,
          message: contactForm.message,
          source: 'miniwebsite_landing',
        }),
      });
      if (!res.ok && res.status !== 202) throw new Error('Failed');
      setSubmitMsg({ type: 'ok', text: t('mwl_contact_success') });
      setContactForm({ name: '', email: '', phone: '', message: '' });
    } catch {
      setSubmitMsg({ type: 'err', text: t('mwl_contact_error') });
    } finally {
      setSubmitting(false);
    }
  };

  const billingLabel = (cycle: UnifiedBillingCycle) => {
    if (cycle === 'monthly') return t('mwl_pricing_monthly', { defaultValue: 'Monthly' });
    if (cycle === 'semiannual') return t('mwl_pricing_6months', { defaultValue: '6 Months' });
    return t('mwl_pricing_yearly', { defaultValue: 'Yearly' });
  };

  const planFeatures = (plan: PaidUnifiedPlanKey): string[] => {
    const raw = t(`mwl_unified_${plan}_features`, { returnObjects: true }) as unknown;
    if (Array.isArray(raw) && raw.length > 0 && raw.every((item) => typeof item === 'string')) {
      return raw as string[];
    }
    return [];
  };

  const openSamplePreview = (kind: keyof typeof SAMPLE_WEBSITES) => {
    const sample = SAMPLE_WEBSITES[kind];
    setSamplePreview({
      url: sample.url,
      title: t(sample.labelKey, { defaultValue: sample.defaultLabel }),
    });
  };

  const renderPricingCard = (plan: PaidUnifiedPlanKey) => {
    const theme = UNIFIED_PLAN_META[plan];
    const displayCurrency = getDisplayCurrencyForLang(resolvedLang);
    const price = getUnifiedPlanPrice(plan, billingCycle, displayCurrency);
    const monthlyRef = getUnifiedPlanPrice(plan, 'monthly', displayCurrency);
    const savings =
      billingCycle === 'yearly'
        ? Math.max(0, Math.round((1 - price / (monthlyRef * 12)) * 100))
        : billingCycle === 'semiannual'
          ? Math.max(0, Math.round((1 - price / (monthlyRef * 6)) * 100))
          : 0;

    return (
      <div
        key={plan}
        className={`mwl-price-card${theme.popular ? ' mwl-price-card--popular' : ''}`}
        style={{ background: theme.soft, borderColor: theme.popular ? theme.accent : '#E2E8F0' }}
      >
        {theme.popular && (
          <span
            style={{
              alignSelf: 'flex-start',
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 0.4,
              textTransform: 'uppercase',
              padding: '5px 10px',
              borderRadius: 999,
              background: `${theme.accent}22`,
              color: theme.accent,
              marginBottom: 10,
            }}
          >
            {t('mwl_pricing_popular', { defaultValue: 'Most Popular' })}
          </span>
        )}
        <h3 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#0F172A' }}>
          {t(`mwl_unified_${plan}_name`, { defaultValue: `${plan.charAt(0).toUpperCase()}${plan.slice(1)} Plan` })}
        </h3>
        <p style={{ margin: '8px 0 0', fontSize: 14, color: '#64748B', lineHeight: 1.5 }}>
          {t(`mwl_unified_${plan}_desc`)}
        </p>
        <div style={{ marginTop: 18, display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 32, fontWeight: 900, color: theme.accent, lineHeight: 1 }}>
            {formatPlanPrice(price, displayCurrency)}
          </span>
          <span style={{ fontSize: 13, color: '#64748B', fontWeight: 600 }}>
            / {billingLabel(billingCycle)}
          </span>
        </div>
        {savings > 0 && (
          <p style={{ margin: '8px 0 0', fontSize: 12, fontWeight: 700, color: '#059669' }}>
            {t('mwl_pricing_save', { defaultValue: 'Save {{percent}}%', percent: savings })}
          </p>
        )}
        <ul style={{ listStyle: 'none', margin: '18px 0 0', padding: 0, flex: 1 }}>
          {planFeatures(plan).map((feature) => (
            <li
              key={feature}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
                fontSize: 14,
                color: '#334155',
                marginBottom: 10,
                lineHeight: 1.45,
              }}
            >
              <span style={{ color: '#10B981', fontWeight: 800, flexShrink: 0 }}>✓</span>
              <span>{feature}</span>
            </li>
          ))}
        </ul>
        <button
          type="button"
          className="mwl-btn mwl-btn--primary"
          style={{ marginTop: 'auto', width: '100%' }}
          onClick={() => setShowSignup(true)}
        >
          {t('mwl_pricing_cta', { defaultValue: 'Start Free Trial' })}
        </button>
      </div>
    );
  };

  return (
    <div className="mwl-root">
      <style>{landingStyles}</style>

      <nav className="mwl-nav" style={{ position: 'sticky' }}>
        <TcardsBrandLogo
          href={TCARDS_HOME_URL}
          className="mwl-brand"
          size={40}
          tagline={t('mwl_nav_tagline', { defaultValue: 'Professional Mini Websites' })}
          taglineClassName="mwl-brand-tag"
        />

        <div className="mwl-nav-row">
          <div className="mwl-nav-desktop">
            <select
              value={resolvedLang}
              onChange={(e) => {
                persistManualLanguage(e.target.value);
                void i18n.changeLanguage(e.target.value);
              }}
              aria-label={t('home_lang_select', { defaultValue: 'Language' })}
              style={{
                padding: '8px 10px',
                border: '1px solid #E2E8F0',
                borderRadius: 8,
                fontSize: 13,
                background: '#FFF',
                cursor: 'pointer',
              }}
            >
              {LANG_OPTIONS.map((l) => (
                <option key={l.code} value={l.code}>
                  {l.flag} {l.label}
                </option>
              ))}
            </select>
            <button type="button" className="mwl-btn mwl-btn--ghost mwl-btn--inline" onClick={() => setShowSignup(true)}>
              {t('mwl_sign_up', { defaultValue: 'Sign up' })}
            </button>
            <button type="button" className="mwl-btn mwl-btn--primary mwl-btn--inline" onClick={goCreate}>
              {t('mwl_sign_in', { defaultValue: 'Sign in' })}
            </button>
          </div>
          <button
            type="button"
            className="mwl-menu-btn"
            aria-expanded={menuOpen}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            onClick={() => setMenuOpen((v) => !v)}
          >
            {menuOpen ? '✕' : '☰'}
          </button>
        </div>

        {menuOpen && (
          <div className="mwl-nav-actions mwl-nav-actions--open">
            <select
              value={resolvedLang}
              onChange={(e) => {
                persistManualLanguage(e.target.value);
                void i18n.changeLanguage(e.target.value);
              }}
              aria-label={t('home_lang_select', { defaultValue: 'Language' })}
              style={{
                padding: '12px 14px',
                border: '1px solid #E2E8F0',
                borderRadius: 10,
                fontSize: 15,
                background: '#FFF',
              }}
            >
              {LANG_OPTIONS.map((l) => (
                <option key={l.code} value={l.code}>
                  {l.flag} {l.label}
                </option>
              ))}
            </select>
            <button type="button" className="mwl-btn mwl-btn--ghost" onClick={() => { setShowSignup(true); setMenuOpen(false); }}>
              {t('mwl_sign_up', { defaultValue: 'Sign up' })}
            </button>
            <button type="button" className="mwl-btn mwl-btn--primary" onClick={() => { goCreate(); setMenuOpen(false); }}>
              {t('mwl_sign_in', { defaultValue: 'Sign in' })}
            </button>
          </div>
        )}
      </nav>

      <section className="mwl-section mwl-section--hero">
        <h1 className="mwl-hero-title">{t('mwl_hero_title')}</h1>
        <p className="mwl-hero-sub">{t('mwl_hero_subtitle')}</p>
        <button type="button" className="mwl-btn mwl-btn--primary mwl-btn--block-desktop" onClick={() => setShowSignup(true)}>
          👉 {t('mwl_hero_cta')}
        </button>
        <p style={{ marginTop: 16, fontSize: 13, color: '#94A3B8' }}>{t('mwl_hero_micro')}</p>
      </section>

      {/* 2 — Problem */}
      <section className="mwl-section mwl-container--narrow" id="problem" style={{ textAlign: 'center' }}>
        <h2 style={{ fontSize: 'clamp(22px, 4vw, 34px)', fontWeight: 800, marginBottom: 16 }}>
          🚨 {t('mwl_problem_title')}
        </h2>
        <p style={{ fontSize: 16, color: '#475569', lineHeight: 1.7, marginBottom: 8 }}>{t('mwl_problem_line1')}</p>
        <p style={{ fontSize: 16, color: '#475569', lineHeight: 1.7, marginBottom: 20 }}>{t('mwl_problem_line2')}</p>
        <p style={{ fontSize: 16, fontWeight: 600, color: '#3B82F6' }}>👉 {t('mwl_problem_solution')}</p>
      </section>

      {/* 3 — Live examples */}
      <section className="mwl-section mwl-section--white" id="samples">
        <div className="mwl-container">
          <h2 style={{ fontSize: 'clamp(22px, 4vw, 34px)', fontWeight: 800, textAlign: 'center', margin: '0 0 12px' }}>
            👀 {t('mwl_samples_title', { defaultValue: 'See Live Examples' })}
          </h2>
          <p style={{ textAlign: 'center', fontSize: 16, color: '#64748B', maxWidth: 680, margin: '0 auto 12px', lineHeight: 1.65 }}>
            {t('mwl_samples_subtitle')}
          </p>
          <p style={{ textAlign: 'center', fontSize: 14, color: '#475569', maxWidth: 680, margin: '0 auto 28px', lineHeight: 1.6 }}>
            {t('mwl_samples_note')}
          </p>

          <div className="mwl-samples-grid">
            {(Object.keys(SAMPLE_WEBSITES) as Array<keyof typeof SAMPLE_WEBSITES>).map((kind) => {
              const sample = SAMPLE_WEBSITES[kind];
              const label = t(sample.labelKey, { defaultValue: sample.defaultLabel });
              return (
                <div
                  key={kind}
                  className="mwl-sample-card"
                  style={{ background: sample.soft, borderColor: `${sample.accent}33` }}
                >
                  <div style={{ fontSize: 40, marginBottom: 12 }}>{sample.icon}</div>
                  <h3 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 800, color: '#0F172A', lineHeight: 1.3 }}>{label}</h3>
                  <p className="mwl-sample-card-desc">
                    {kind === 'product'
                      ? t('mwl_sample_product_desc')
                      : t('mwl_sample_service_desc')}
                  </p>
                  <button
                    type="button"
                    className="mwl-sample-btn"
                    style={{ background: `linear-gradient(135deg, ${sample.accent}, ${kind === 'product' ? '#4F46E5' : '#A855F7'})` }}
                    onClick={() => openSamplePreview(kind)}
                  >
                    {label} →
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 4 — Benefits */}
      <section className="mwl-section mwl-section--white" id="benefits">
        <div className="mwl-container">
          <h2 style={{ fontSize: 'clamp(22px, 4vw, 34px)', fontWeight: 800, textAlign: 'center', marginBottom: 32 }}>
            💡 {t('mwl_value_title')}
          </h2>
          <div className="mwl-grid-benefits">
            {[
              { icon: '🛍️', key: 'sell' },
              { icon: '💰', key: 'paid' },
              { icon: '📦', key: 'track' },
              { icon: '📩', key: 'leads' },
              { icon: '🔗', key: 'link' },
              { icon: '⚡', key: 'fast' },
            ].map(({ icon, key }) => (
              <div
                key={key}
                style={{
                  background: '#F8FAFC',
                  borderRadius: 16,
                  padding: '24px 20px',
                  textAlign: 'center',
                  border: '1px solid #E2E8F0',
                }}
              >
                <div style={{ fontSize: 34, marginBottom: 12 }}>{icon}</div>
                <h3 style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>{t(`mwl_value_${key}_title`)}</h3>
                <p style={{ fontSize: 14, color: '#64748B', lineHeight: 1.5, margin: 0 }}>{t(`mwl_value_${key}_desc`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5 — How it works */}
      <section className="mwl-section" id="how">
        <div className="mwl-container--narrow">
          <h2 style={{ fontSize: 'clamp(22px, 4vw, 34px)', fontWeight: 800, textAlign: 'center', marginBottom: 32 }}>
            ⚡ {t('mwl_how_title')}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            {[1, 2, 3, 4].map((n) => (
              <div key={n} className="mwl-step">
                <div className="mwl-step-num">{n}</div>
                <span style={{ fontSize: 16, fontWeight: 500, lineHeight: 1.5, paddingTop: 8 }}>{t(`mwl_how_step${n}`)}</span>
              </div>
            ))}
          </div>
          <p style={{ marginTop: 24, fontSize: 16, fontWeight: 700, color: '#10B981', textAlign: 'center' }}>
            ✅ {t('mwl_how_done')}
          </p>
        </div>
      </section>

      {/* 6 — Trust / Why Tcards */}
      <section className="mwl-section mwl-section--dark" id="trust">
        <div className="mwl-container--narrow" style={{ textAlign: 'center' }}>
          <h2 style={{ fontSize: 'clamp(22px, 4vw, 34px)', fontWeight: 800, marginBottom: 12 }}>
            🌍 {t('mwl_trust_title', { defaultValue: 'Why Tcards' })}
          </h2>
          <p style={{ fontSize: 16, color: '#94A3B8', marginBottom: 28, lineHeight: 1.65 }}>{t('mwl_trust_subtitle')}</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, textAlign: 'left', maxWidth: 460, margin: '0 auto' }}>
            {['africa', 'affordable', 'paid', 'tracking', 'simple', 'professional'].map((k) => (
              <div key={k} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <span style={{ fontSize: 20, flexShrink: 0 }}>✅</span>
                <span style={{ fontSize: 15, lineHeight: 1.5 }}>{t(`mwl_trust_${k}`)}</span>
              </div>
            ))}
          </div>
          <p style={{ marginTop: 28, fontSize: 16, fontWeight: 600, color: '#E2E8F0' }}>{t('mwl_trust_closing')}</p>
        </div>
      </section>

      {/* 7 — Pricing */}
      <section className="mwl-section mwl-section--white" id="pricing">
        <div className="mwl-container mwl-container--pricing">
          <h2 style={{ fontSize: 'clamp(24px, 5vw, 36px)', fontWeight: 800, textAlign: 'center', margin: '0 0 10px' }}>
            💳 {t('mwl_pricing_title', { defaultValue: 'Affordable Publishing Plans' })}
          </h2>
          <p style={{ textAlign: 'center', fontSize: 16, color: '#64748B', maxWidth: 620, margin: '0 auto 24px', lineHeight: 1.6 }}>
            {t('mwl_pricing_subtitle')}
          </p>

          <div className="mwl-billing-toggle" role="tablist" aria-label={t('mwl_pricing_billing_label', { defaultValue: 'Billing cycle' })}>
            {(['monthly', 'semiannual', 'yearly'] as const).map((cycle) => (
              <button
                key={cycle}
                type="button"
                role="tab"
                aria-selected={billingCycle === cycle}
                className={`mwl-billing-btn${billingCycle === cycle ? ' mwl-billing-btn--active' : ''}`}
                onClick={() => setBillingCycle(cycle)}
              >
                {billingLabel(cycle)}
              </button>
            ))}
          </div>

          <div className="mwl-pricing-grid">
            {PAID_UNIFIED_PLANS.map((plan) => renderPricingCard(plan))}
          </div>

          <p style={{ textAlign: 'center', fontSize: 13, color: '#94A3B8', marginTop: 18, lineHeight: 1.5 }}>
            {t('mwl_pricing_note')}
          </p>
          <div style={{ textAlign: 'center', marginTop: 24 }}>
            <button type="button" className="mwl-btn mwl-btn--primary mwl-btn--block-desktop" onClick={() => setShowSignup(true)}>
              👉 {t('mwl_final_cta')}
            </button>
          </div>
        </div>
      </section>

      {/* 8 — Target users */}
      <section className="mwl-section" id="who">
        <div className="mwl-container--narrow" style={{ textAlign: 'center' }}>
          <h2 style={{ fontSize: 'clamp(22px, 4vw, 34px)', fontWeight: 800, marginBottom: 28 }}>
            🎯 {t('mwl_who_title')}
          </h2>
          <div className="mwl-chip-row">
            {['shops', 'services', 'freelancers', 'sme', 'entrepreneurs'].map((k) => (
              <span key={k} className="mwl-chip">
                {t(`mwl_who_${k}`)}
              </span>
            ))}
          </div>
          <p style={{ marginTop: 24, fontSize: 16, fontWeight: 600, color: '#475569' }}>{t('mwl_who_closing')}</p>
        </div>
      </section>

      {/* 9 — FAQ */}
      <section className="mwl-section mwl-section--white" id="faq">
        <div className="mwl-container--narrow">
          <h2 style={{ fontSize: 'clamp(22px, 4vw, 30px)', fontWeight: 800, textAlign: 'center', marginBottom: 28 }}>
            ❓ {t('mwl_faq_title')}
          </h2>
          {['coding', 'sell', 'paid', 'track', 'contact', 'free', 'pricing'].map((k) => (
            <div key={k} className="mwl-faq">
              <h4 style={{ fontWeight: 700, fontSize: 15, marginBottom: 8 }}>{t(`mwl_faq_${k}_q`, { defaultValue: k })}</h4>
              <p style={{ fontSize: 14, color: '#64748B', margin: 0, lineHeight: 1.55 }}>{t(`mwl_faq_${k}_a`, { defaultValue: '' })}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 10 — Final CTA */}
      <section className="mwl-section mwl-section--cta" id="get-started">
        <h2 style={{ fontSize: 'clamp(24px, 5vw, 40px)', fontWeight: 900, marginBottom: 16 }}>
          🚀 {t('mwl_final_title')}
        </h2>
        <p style={{ fontSize: 17, marginBottom: 28, opacity: 0.92, lineHeight: 1.6 }}>{t('mwl_final_subtitle')}</p>
        <button type="button" className="mwl-btn mwl-btn--white mwl-btn--block-desktop" onClick={() => setShowSignup(true)}>
          👉 {t('mwl_final_cta')}
        </button>
        <p style={{ marginTop: 16, fontSize: 13, opacity: 0.85 }}>{t('mwl_final_micro')}</p>
      </section>

      {/* 11 — Contact */}
      <section className="mwl-section" id="contact">
        <div className="mwl-container--contact">
          <h2 style={{ fontSize: 'clamp(22px, 4vw, 30px)', fontWeight: 800, textAlign: 'center', marginBottom: 8 }}>
            📩 {t('mwl_contact_title')}
          </h2>
          <p style={{ textAlign: 'center', fontSize: 14, color: '#64748B', marginBottom: 24, lineHeight: 1.5 }}>
            {t('mwl_contact_subtitle')}
          </p>
          <form className="mwl-form" onSubmit={handleContact}>
            <input
              className="mwl-input"
              placeholder={t('mwl_contact_name')}
              value={contactForm.name}
              onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
              required
              autoComplete="name"
            />
            <input
              className="mwl-input"
              type="email"
              placeholder={t('mwl_contact_email')}
              value={contactForm.email}
              onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
              required
              autoComplete="email"
              inputMode="email"
            />
            <input
              className="mwl-input"
              type="tel"
              placeholder={t('mwl_contact_phone')}
              value={contactForm.phone}
              onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
              required
              autoComplete="tel"
              inputMode="tel"
            />
            <textarea
              className="mwl-textarea"
              placeholder={t('mwl_contact_message')}
              value={contactForm.message}
              onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
              required
            />
            {submitMsg && (
              <p style={{ fontSize: 13, fontWeight: 600, color: submitMsg.type === 'ok' ? '#10B981' : '#EF4444', margin: 0 }}>
                {submitMsg.text}
              </p>
            )}
            <button type="submit" className="mwl-btn mwl-btn--primary" disabled={submitting} style={{ opacity: submitting ? 0.7 : 1 }}>
              {submitting ? '⏳...' : t('mwl_contact_send')}
            </button>
          </form>
        </div>
      </section>

      <footer style={{ padding: '28px 16px', background: '#0F172A', color: '#94A3B8', textAlign: 'center' }}>
        <p style={{ fontSize: 14, marginBottom: 8 }}>{t('mwl_footer_text')}</p>
        <p style={{ fontSize: 12, opacity: 0.6, margin: 0 }}>Powered by Tcards</p>
      </footer>

      <CardOwnerSignupModal open={showSignup} onClose={() => setShowSignup(false)} />

      {samplePreview && (
        <div
          className="mwl-preview-overlay"
          role="dialog"
          aria-modal="true"
          aria-label={samplePreview.title}
          onClick={() => setSamplePreview(null)}
        >
          <div className="mwl-preview-panel" onClick={(e) => e.stopPropagation()}>
            <div className="mwl-preview-header">
              <div style={{ minWidth: 0 }}>
                <p style={{ margin: 0, fontSize: 15, fontWeight: 800, color: '#1E293B' }}>{samplePreview.title}</p>
                <p style={{ margin: '2px 0 0', fontSize: 11, color: '#64748B', wordBreak: 'break-all' }}>{samplePreview.url}</p>
              </div>
              <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                <a
                  href={samplePreview.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    padding: '8px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                    border: '1px solid #BFDBFE', color: '#2563EB', background: '#FFF', textDecoration: 'none',
                  }}
                >
                  {t('mwl_sample_open_tab', { defaultValue: 'Open in new tab' })}
                </a>
                <button
                  type="button"
                  onClick={() => setSamplePreview(null)}
                  aria-label={t('mwl_sample_preview_close', { defaultValue: 'Close preview' })}
                  style={{
                    width: 36, height: 36, border: 'none', borderRadius: 8, background: '#F1F5F9',
                    color: '#475569', fontSize: 18, cursor: 'pointer', lineHeight: 1,
                  }}
                >
                  ✕
                </button>
              </div>
            </div>
            <iframe
              className="mwl-preview-frame"
              src={samplePreview.url}
              title={samplePreview.title}
              sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default MiniWebsiteLanding;
